<template>
  <a-modal
    :title="`Loads for Company: ${companyName}`"
    :visible="visible"
    width="90%"
    :bodyStyle="{ padding: '16px' }"
    :mask-closable="true"
    :keyboard="true"
    ok-text="Close"
    :show-cancel="false"
    @ok="handleClose"
    @cancel="handleClose"
  >
    <!-- Company Information Header -->
    <a-card size="small" style="margin-bottom: 16px;" v-if="companyInfo && Object.keys(companyInfo).length">
      <template #title>
        <span>Company Information</span>
        <a-button 
          type="link" 
          @click="handleClose" 
          style="float: right; color: #1890ff;"
          title="Close"
        >
          <a-icon type="close" />
        </a-button>
      </template>
      <a-row :gutter="16">
        <a-col :span="6">
          <p><strong>Company Name:</strong> {{ companyInfo.name || 'N/A' }}</p>
        </a-col>
        <a-col :span="6">
          <p><strong>Address:</strong> {{ companyInfo.address || 'N/A' }}</p>
        </a-col>
        <a-col :span="6">
          <p><strong>Total Loads:</strong> {{ loads.length }}</p>
        </a-col>
        <a-col :span="6">
          <p><strong>Total Bids:</strong> {{ totalBids }}</p>
        </a-col>
      </a-row>
      <a-row :gutter="16" v-if="loads.length > 0 && loads[0].company_bids && loads[0].company_bids.length > 0">
        <a-col :span="6">
          <p><strong>Company from Bids:</strong> {{ (loads[0].company_bids[0].bidder.company && loads[0].company_bids[0].bidder.company.name) || 'N/A' }}</p>
        </a-col>
        <a-col :span="6">
          <p><strong>Company Address:</strong> {{ (loads[0].company_bids[0].bidder.company && loads[0].company_bids[0].bidder.company.address) || 'N/A' }}</p>
        </a-col>
      </a-row>
    </a-card>



    <!-- Loads Table -->
    <a-card title="Loads with Company Bids" size="small">
      <a-table
        :columns="loadColumns"
        :data-source="loads"
        :loading="loading"
        :pagination="paginationConfig"
        rowKey="id"
        size="small"
      >
        <!-- External Load ID Column -->
        <template slot="external_load_id" slot-scope="text, record">
          <span style="font-weight: bold; color: #1890ff;">
            {{ record.load.external_load_id || 'N/A' }}
          </span>
        </template>
        
        <!-- Origin Column -->
        <template slot="origin" slot-scope="text, record">
          {{ getLocationString(record.load.origin_city_name, record.load.origin_state_code) }}
        </template>
        
        <!-- Destination Column -->
        <template slot="destination" slot-scope="text, record">
          {{ getLocationString(record.load.destination_city_name, record.load.destination_state_code) }}
        </template>
        
        <!-- Distance Column -->
        <template slot="total_distance" slot-scope="text, record">
          {{ record.load.total_distance || 'N/A' }} miles
        </template>
        
        <!-- Pickup Date Column -->
        <template slot="scheduled_pickup_date_time" slot-scope="text, record">
          {{ formatDate(record.load.scheduled_pickup_date_time) }}
        </template>
        
        <!-- Status Column -->
        <template slot="status" slot-scope="text, record">
          <a-tag :color="getStatusColor(record.load.status)">
            {{ (record.load.status || '').toUpperCase() }}
          </a-tag>
        </template>
        
        <!-- Company Bids Column -->
        <template slot="company_bids" slot-scope="text, record">
          <div v-if="record.company_bids && record.company_bids.length > 0">
            <div v-for="bid in record.company_bids" :key="bid.id" style="margin-bottom: 8px;">
              <a-tag color="blue">
                {{ bid.bidder.full_name }}: ${{ formatCurrency(bid.offer_amount) }}
              </a-tag>
            </div>
          </div>
          <span v-else style="color: #999;">No bids</span>
        </template>
        
        <!-- Created At Column -->
        <template slot="created_at" slot-scope="text, record">
          {{ formatDate(record.load.created_at) }}
        </template>
      </a-table>
    </a-card>
  </a-modal>
</template>

<script>
import loadAPI from '../../api/load';

export default {
  name: 'CompanyLoadsPopup',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: [String, Number],
      default: null
    },
    companyInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loads: [],
      loading: false,
      pageSize: 10,
      currentPage: 1,
      totalCount: 0,
    }
  },
  computed: {
    companyName() {
      return this.companyInfo.name || 'Unknown Company';
    },
    totalBids() {
      return this.loads.reduce((total, loadWithBids) => {
        return total + (loadWithBids.company_bids ? loadWithBids.company_bids.length : 0);
      }, 0);
    },
    paginationConfig() {
      return {
        pageSize: this.pageSize,
        current: this.currentPage,
        total: this.totalCount,
        onChange: this.handlePageChange,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} loads`,
      };
    },
    loadColumns() {
      return [
        {
          title: 'Load ID',
          dataIndex: 'load.external_load_id',
          width: 120,
          scopedSlots: { customRender: 'external_load_id' },
        },
        {
          title: 'Origin',
          dataIndex: 'load.origin',
          width: 150,
          scopedSlots: { customRender: 'origin' },
        },
        {
          title: 'Destination',
          dataIndex: 'load.destination',
          width: 150,
          scopedSlots: { customRender: 'destination' },
        },
        {
          title: 'Distance',
          dataIndex: 'load.total_distance',
          width: 100,
          scopedSlots: { customRender: 'total_distance' },
        },
        {
          title: 'Pickup Date',
          dataIndex: 'load.scheduled_pickup_date_time',
          width: 120,
          scopedSlots: { customRender: 'scheduled_pickup_date_time' },
        },
        {
          title: 'Status',
          dataIndex: 'load.status',
          width: 100,
          scopedSlots: { customRender: 'status' },
        },
        {
          title: 'Company Bids',
          dataIndex: 'company_bids',
          width: 200,
          scopedSlots: { customRender: 'company_bids' },
        },
        {
          title: 'Created At',
          dataIndex: 'load.created_at',
          width: 120,
          scopedSlots: { customRender: 'created_at' },
        },
      ];
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.companyId) {
        this.fetchCompanyLoads();
      }
    }
  },
  methods: {
    async fetchCompanyLoads() {
      if (!this.companyId) return;
      
      this.loading = true;
      try {
        const response = await loadAPI.getLoadsWithBidsForAdmin({
          company_id: this.companyId,
          page: this.currentPage,
          limit: this.pageSize
        });
        
        if (response && response.data) {
          this.loads = response.data;
          this.totalCount = response.pagination ? response.pagination.total : response.data.length;
        }
      } catch (error) {
        console.error('Error fetching company loads:', error);
        this.$message.error('Failed to fetch company loads');
      } finally {
        this.loading = false;
      }
    },
    
    handlePageChange(page, pageSize) {
      this.currentPage = page;
      this.pageSize = pageSize;
      this.fetchCompanyLoads();
    },
    
    handleClose() {
      this.$emit('close');
    },
    
    getLocationString(city, state) {
      if (city && state) {
        return `${city}, ${state}`;
      } else if (city) {
        return city;
      } else if (state) {
        return state;
      }
      return 'N/A';
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString();
      } catch (error) {
        return dateString;
      }
    },
    
    formatCurrency(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toFixed(2);
    },
    
    getStatusColor(status) {
      const statusColors = {
        'active': 'green',
        'completed': 'brown',
        'cancelled': 'red',
        'pending': 'orange',
        'in_progress': 'cyan',
        'auction_lost': 'red',
        'auction_won': 'green'
      };
      return statusColors[status?.toLowerCase()] || 'default';
    }
  }
}
</script>

<style scoped>
.ant-table-small .ant-table-thead > tr > th {
  padding: 8px;
  font-size: 12px;
}

.ant-table-small .ant-table-tbody > tr > td {
  padding: 8px;
  font-size: 12px;
}
</style>
